/**
 * Surf API 调用封装
 * 所有请求走 /api/surf?path=... 服务端透传，不暴露 API Key。
 *
 * 文档：https://agents.asksurf.ai/docs
 * 免费额度：每 IP 每天 30 credits；超额按量付费
 */

const PROXY = process.env.REACT_APP_PROXY || '';

/**
 * 底层请求函数
 * @param {string} path  Surf API 路径，如 /social/mindshare
 * @param {Object} params  查询参数对象
 * @returns {Promise<any>}  Surf API 返回的 JSON 数据
 */
async function surfFetch(path, params = {}) {
  const qs = new URLSearchParams({ path, ...params }).toString();
  const res = await fetch(`${PROXY}/api/surf?${qs}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Surf API error ${res.status}`);
  }
  return res.json();
}

// ─── 社交数据 ────────────────────────────────────────────────────────────────

/**
 * Twitter 心智份额（Mindshare）
 * 返回某项目在 Web3 Twitter 讨论中的占比时序
 *
 * @param {string} q  项目名，如 "ethereum"、"uniswap"（不是代币符号）
 * @param {'5m'|'1h'|'1d'|'7d'} interval  时间桶粒度（必填）
 */
export async function getSocialMindshare(q, interval = '1d') {
  return surfFetch('/social/mindshare', { q, interval });
}

/**
 * 聪明钱关注者历史
 * 返回高价值账户（KOL/机构）关注该项目 Twitter 的趋势
 *
 * @param {string} q  项目名
 * @param {'5m'|'1h'|'1d'|'7d'} interval
 */
export async function getSmartFollowers(q, interval = '1d') {
  return surfFetch('/social/smart-followers-history', { q, interval });
}

// ─── 代币持仓 ────────────────────────────────────────────────────────────────

/**
 * 代币大户持仓分布（带标签：交易所/基金/鲸鱼）
 *
 * @param {string} contract  合约地址（0x...）
 * @param {string} chain     链名，如 "ethereum"
 * @param {number} limit     返回持仓人数，默认 10
 */
export async function getTokenHolders(contract, chain = 'ethereum', limit = 10) {
  return surfFetch('/token/holders', { contract, chain, limit });
}

// ─── VC 基金数据 ──────────────────────────────────────────────────────────────

/**
 * VC 基金详情（AUM、阶段偏好、组合规模）
 *
 * @param {string} q  基金名称，如 "binance-labs"、"a16z"
 */
export async function getFundDetail(q) {
  return surfFetch('/fund/detail', { q });
}

/**
 * VC 基金持仓组合（各项目 + 入场时间）
 *
 * @param {string} q      基金名，如 "a16z"、"binance-labs"
 * @param {number} limit  返回条数，默认 20
 * @param {boolean} is_lead  只看领投记录
 */
export async function getFundPortfolio(q, limit = 20, is_lead = null) {
  const params = { q, limit };
  if (is_lead !== null) params.is_lead = is_lead;
  return surfFetch('/fund/portfolio', params);
}

/**
 * VC 排行榜（Top 基金列表）
 */
export async function getFundRanking() {
  return surfFetch('/fund/ranking');
}

// ─── 新闻 ────────────────────────────────────────────────────────────────────

/**
 * 项目最新新闻
 *
 * @param {string} project   项目名，逗号分隔多个，如 "bitcoin,ethereum"
 * @param {number} limit     返回条数，默认 10，最大 50
 * @param {'recency'|'trending'} sort_by  排序方式
 */
export async function getNewsFeed(project, limit = 10, sort_by = 'recency') {
  return surfFetch('/news/feed', { project, limit, sort_by });
}

// ─── 市场宏观 ─────────────────────────────────────────────────────────────────

/**
 * 链上宏观指标（NUPL / MVRV / SOPR）
 *
 * @param {'BTC'|'ETH'} symbol
 * @param {'7d'|'30d'|'90d'} interval
 */
export async function getMarketIndicators(symbol = 'BTC', interval = '30d') {
  return surfFetch('/market/indicators', { symbol, interval });
}

/**
 * ETF 资金流向
 *
 * @param {'BTC'|'ETH'} symbol
 */
export async function getEtfFlows(symbol = 'BTC') {
  return surfFetch('/market/etf', { symbol });
}

// ─── 全局搜索 ─────────────────────────────────────────────────────────────────

/**
 * 通用网页搜索。
 *
 * 2026-07-09：这个文件里原来有个 surfSearch(query) 打 `/search`，一直没人调用过——
 * 实测 `/search` 在 Surf 网关上是 404，Surf 没有单一的"跨实体全文搜索"端点，
 * 每类实体各自有专属搜索路径（/search/web /search/project /search/fund
 * /search/wallet /search/social-people /search/news 等，见
 * https://agents.asksurf.ai/llms-full.txt）。这里改成对应真实存在的 /search/web，
 * 用于 DuckDuckGo 被限流后 Early Bird wiki 找创始人/公司背景/竞品的替代数据源。
 *
 * @param {string} q  搜索查询
 */
export async function surfWebSearch(q) {
  return surfFetch('/search/web', { q });
}
